import { useState, useEffect } from "react";
import secureStorage from '../../utils/secureStorage';
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu, X, Baby, Users, Palette, Utensils, FileText, TrendingUp, Scale, GraduationCap, UserCheck, FileSpreadsheet, Settings, Home, ShieldCheck, ChevronDown, ChevronRight, BookOpen, ClipboardList, Receipt, FilePlus, CalendarDays, Star, BarChart3, User, Heart, Camera, Shield, ArrowUpCircle, CheckCircle, Candy, Lollipop, NotebookPen } from "lucide-react";
import { ParentIcon, TeacherIcon } from "../ui/AnimatedElements";
import { getLocalDateString } from "../../lib/utils";
import { getAppName } from "../../config/branding";

const _brandLen = getAppName().length;
const deskBrandSize = _brandLen > 22 ? "text-base" : _brandLen > 14 ? "text-xl" : "text-2xl";
const mobBrandSize = _brandLen > 22 ? "text-sm" : _brandLen > 14 ? "text-base" : "text-lg";

// Kawalan Module Global: map path -> module key
import { getModuleForPath, useModuleAccess } from "../../hooks/useModuleAccess";

// Menu items for different roles
const getMenuItems = (role, hasAccess = () => true, isPathAllowed = () => true) => {
  const commonItems = [
    { label: "Dashboard", path: "/", icon: Home, featureId: null },
  ];

  const aktivitiHarianGroup = {
    label: "AKTIVITI HARIAN",
    icon: ClipboardList,
    isGroup: true,
    children: [
      { label: "Kelas", path: "/classes", icon: Users, featureId: "children" },
      { label: "Aktiviti", path: "/activities", icon: Palette, featureId: "activities" },
      { label: "Log Harian", path: "/daily-logs", icon: FileText, featureId: "daily_logs" },
      { label: "Menu", path: "/nutrition", icon: Utensils, featureId: "nutrition" },
    ]
  };

  const rekodPelajarGroupFull = {
    label: "REKOD PELAJAR",
    icon: BookOpen,
    isGroup: true,
    children: [
      { label: "Kanak-Kanak", path: "/children", icon: Baby, featureId: "children" },
      { label: "Kehadiran", path: "/attendance", icon: CheckCircle, featureId: "attendance" },
      { label: "Pendaftaran", path: "/child-registration", icon: FilePlus, featureId: "children" },
      { label: "Perkembangan", path: "/development", icon: TrendingUp, featureId: "development" },
      { label: "Status BMI", path: "/nutritional-status", icon: Scale, featureId: "nutritional_status" },
    ]
  };

  const rekodPelajarGroupParent = {
    label: "REKOD PELAJAR",
    icon: BookOpen,
    isGroup: true,
    children: [
      { label: "Kanak-Kanak", path: "/children", icon: Baby, featureId: "children" },
      { label: "Kehadiran", path: "/attendance", icon: CheckCircle, featureId: "attendance" },
      { label: "Perkembangan", path: "/development", icon: TrendingUp, featureId: "development" },
      { label: "Status BMI", path: "/nutritional-status", icon: Scale, featureId: "nutritional_status" },
    ]
  };

  const parentItems = [
    { label: "Pendaftaran Anak", path: "/child-registration", icon: FilePlus, featureId: null },
    aktivitiHarianGroup,
    rekodPelajarGroupParent,
    { label: "Album Koleksi", path: "/album", icon: Camera, featureId: null },
    { label: "Takwim", path: "/takwim", icon: CalendarDays, featureId: "takwim" },
    { label: "Invois & Bil", path: "/billing", icon: Receipt, featureId: "invoices" },
    { label: "Panduan Apps", path: "/panduan-ibu-bapa", icon: BookOpen, featureId: null },
    { label: "Tetapan Profil", path: "/settings", icon: Settings, featureId: null },
  ];

  // Rekod Pelajar untuk guru (TANPA Pendaftaran)
  const rekodPelajarGroupTeacher = {
    label: "REKOD PELAJAR",
    icon: BookOpen,
    isGroup: true,
    children: [
      { label: "Kanak-Kanak", path: "/children", icon: Baby, featureId: "children" },
      { label: "Kehadiran", path: "/attendance", icon: CheckCircle, featureId: "attendance" },
      { label: "Perkembangan", path: "/development", icon: TrendingUp, featureId: "development" },
      { label: "Status BMI", path: "/nutritional-status", icon: Scale, featureId: "nutritional_status" },
    ]
  };

  const teacherItems = [
    aktivitiHarianGroup,
    rekodPelajarGroupTeacher,
    { label: "RPA", path: "/rpa", icon: NotebookPen, featureId: null },
    { label: "Album Koleksi", path: "/album-manage", icon: Camera, featureId: null },
    { label: "Takwim", path: "/takwim", icon: CalendarDays, featureId: "takwim" },
    { label: "Tetapan Profil", path: "/settings", icon: Settings, featureId: null },
  ];

  const adminItems = [
    { label: "Pengurusan Guru", path: "/manage-teachers", icon: GraduationCap },
    { label: "Pengurusan Ibu Bapa", path: "/manage-parents", icon: UserCheck },
    { label: "Naik Kelas", path: "/class-transition", icon: ArrowUpCircle },
    aktivitiHarianGroup,
    rekodPelajarGroupFull,
    { label: "Semakan RPA", path: "/rpa", icon: NotebookPen },
    { label: "Invois & Bil", path: "/billing", icon: Receipt, featureId: "invoices" },
    { label: "Sistem Invois", path: "/invoice-system", icon: FileText, featureId: null },
    { label: "Album Koleksi", path: "/album-manage", icon: Camera },
    { label: "Takwim", path: "/takwim", icon: CalendarDays, featureId: "takwim" },
    { label: "Laporan", path: "/reports", icon: FileSpreadsheet },
    { label: "Panduan Admin", path: "/panduan-admin", icon: BookOpen },
    { label: "Tetapan Profil", path: "/settings", icon: Settings },
  ];

  const branchManagerItems = [
    { label: "Pengesahan", path: "/pending-approval", icon: UserCheck },
    { label: "Log Audit", path: "/audit-logs", icon: Shield },
    { label: "Naik Kelas", path: "/class-transition", icon: ArrowUpCircle },
    aktivitiHarianGroup,
    rekodPelajarGroupFull,
    { label: "Semakan RPA", path: "/rpa", icon: NotebookPen },
    { label: "Invois & Bil", path: "/billing", icon: Receipt, featureId: "invoices" },
    { label: "Sistem Invois", path: "/invoice-system", icon: FileText, featureId: null },
    { label: "Album Koleksi", path: "/album-manage", icon: Camera },
    { label: "Takwim", path: "/takwim", icon: CalendarDays, featureId: "takwim" },
    { label: "Tetapan Profil", path: "/settings", icon: Settings },
  ];

  const superadminItems = [
    { label: "Pengesahan", path: "/pending-approval", icon: UserCheck },
    { label: "Log Audit", path: "/audit-logs", icon: Shield },
    { label: "Pengurusan Guru", path: "/manage-teachers", icon: GraduationCap },
    { label: "Pengurusan Ibu Bapa", path: "/manage-parents", icon: UserCheck },
    { label: "Naik Kelas", path: "/class-transition", icon: ArrowUpCircle },
    aktivitiHarianGroup,
    rekodPelajarGroupFull,
    { label: "Semakan RPA", path: "/rpa", icon: NotebookPen },
    { label: "Invois & Bil", path: "/billing", icon: Receipt, featureId: "invoices" },
    { label: "Sistem Invois", path: "/invoice-system", icon: FileText, featureId: null },
    { label: "Album Koleksi", path: "/album-manage", icon: Camera },
    { label: "Takwim", path: "/takwim", icon: CalendarDays, featureId: "takwim" },
    { label: "Laporan", path: "/reports", icon: FileSpreadsheet },
    { label: "Panduan Admin", path: "/panduan-admin", icon: BookOpen },
    { label: "Tetapan Profil", path: "/settings", icon: Settings },
  ];

  const filterMenuItem = (item) => {
    if (item.isGroup) {
      const filteredChildren = item.children.filter(child =>
        (!child.featureId || hasAccess(child.featureId)) && isPathAllowed(child.path)
      );
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    }
    if (item.featureId && !hasAccess(item.featureId)) return null;
    if (item.path && !isPathAllowed(item.path)) return null;
    return item;
  };

  let items = [...commonItems];
  switch (role) {
    case "ibu_bapa": items = [...commonItems, ...parentItems.map(filterMenuItem).filter(Boolean)]; break;
    case "guru": items = [...commonItems, ...teacherItems.map(filterMenuItem).filter(Boolean)]; break;
    case "admin": items = [...commonItems, ...adminItems.map(filterMenuItem).filter(Boolean)]; break;
    case "branch_manager": items = [...commonItems, ...branchManagerItems.map(filterMenuItem).filter(Boolean)]; break;
    case "superadmin": items = [...commonItems, ...superadminItems.map(filterMenuItem).filter(Boolean)]; break;
    default: break;
  }
  return items;
};

const getRoleLabel = (role) => {
  switch (role) {
    case "ibu_bapa": return { icon: ParentIcon, text: "Ibu Bapa" };
    case "guru": return { icon: TeacherIcon, text: "Pendidik" };
    case "admin": return { emoji: "👨‍💼", text: "Penyelia" };
    case "superadmin": return { emoji: "👑", text: "Superadmin" };
    case "branch_manager": return { emoji: "🏢", text: "Pengurus Cawangan" };
    default: return { emoji: "👤", text: role };
  }
};

// Helper component to render role label
const RoleLabelDisplay = ({ role }) => {
  const roleInfo = getRoleLabel(role);
  if (roleInfo.icon) {
    const IconComponent = roleInfo.icon;
    return (
      <span className="inline-flex items-center gap-1">
        <IconComponent size="sm" /> {roleInfo.text}
      </span>
    );
  }
  return <span>{roleInfo.emoji} {roleInfo.text}</span>;
};

// Rainbow colors for menu items
const menuColors = ['#F04A83', '#FFC93C', '#FFDD75', '#6FE7DD', '#7A5FB5', '#9178D6', '#F783AC', '#22B8CF'];

export default function Sidebar({ user, onLogout, hasAccess = () => true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    'AKTIVITI HARIAN': false,
    'REKOD PELAJAR': false
  });
  
  // Kawalan Module Global - sembunyikan menu module yang OFF
  const { isPathAllowed, isModuleOn } = useModuleAccess(user?.role);
  
  // Sorok "Invois & Bil" ibu bapa bila KEDUA-DUA sistem invois dimatikan
  // (Sistem Invois dalaman + Invoiless)
  const parentBillingHidden = user?.role === 'ibu_bapa'
    && !isModuleOn('invoice_system')
    && !isModuleOn('invoiless');
  
  const menuItems = getMenuItems(user?.role, hasAccess, isPathAllowed)
    .filter(item => !(parentBillingHidden && item.path === '/billing'));

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const toggleGroup = (groupLabel) => {
    setExpandedGroups(prev => ({ ...prev, [groupLabel]: !prev[groupLabel] }));
  };

  const isGroupActive = (group) => {
    if (!group.isGroup || !group.children) return false;
    return group.children.some(child => isActive(child.path));
  };

  const renderMenuItem = (item, isMobile = false, isChild = false, colorIndex = 0) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const color = menuColors[colorIndex % menuColors.length];
    
    return (
      <button
        key={item.path + item.label}
        onClick={() => {
          navigate(item.path);
          if (isMobile) setMenuOpen(false);
        }}
        className={`candy-menu-item w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-left ${isChild ? 'pl-10' : ''} font-bold`}
        style={active ? { 
          background: 'linear-gradient(135deg, #F04A83 0%, #9178D6 100%)',
          color: '#FFFFFF',
          boxShadow: '0 6px 16px rgba(240,74,131,0.3)'
        } : { color: '#7B75A0' }}
      >
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300"
          style={{ background: active ? 'rgba(255,255,255,0.25)' : '#FDEBF3' }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: active ? '#FFFFFF' : color }} />
        </span>
        <span className="text-sm" style={{ fontFamily: "'Fredoka', 'Comfortaa', sans-serif" }}>{item.label}</span>
        {active && <Candy className="w-4 h-4 ml-auto" style={{ color: '#FFE9A3', animation: 'candySpin 2.5s ease-in-out infinite' }} />}
      </button>
    );
  };

  const renderMenuGroup = (group, isMobile = false, groupIndex = 0) => {
    const Icon = group.icon;
    const isExpanded = expandedGroups[group.label];
    const groupActive = isGroupActive(group);
    const color = menuColors[(groupIndex + 3) % menuColors.length];
    
    return (
      <div key={group.label} className="mb-2">
        <button
          onClick={() => toggleGroup(group.label)}
          className="w-full flex items-center justify-between px-4 py-2.5 rounded-2xl transition-all duration-300 hover:bg-gray-100"
          style={groupActive ? { backgroundColor: `${color}15` } : {}}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4" style={{ color }} />
            <span className="text-xs font-black uppercase tracking-wide" style={{ color: groupActive ? color : '#6B7280' }}>
              {group.label}
            </span>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        
        {isExpanded && (
          <div className="mt-1 space-y-1">
            {group.children.map((child, i) => renderMenuItem(child, isMobile, true, groupIndex + i))}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [menuOpen]);

  return (
    <>
      {/* Mobile Overlay */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" onClick={() => setMenuOpen(false)} />
      )}

      {/* Mobile Header */}
      <div 
        className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-lg z-50 px-4"
        style={{ 
          paddingTop: 'max(12px, calc(env(safe-area-inset-top, 0px) + 8px))',
          paddingBottom: '12px'
        }}
      >
        <div className="flex items-center justify-between">
          <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200">
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
          
          <div className="flex items-center gap-2 absolute left-1/2 transform -translate-x-1/2">
            <img src="/logo-destiny.png" alt="Logo" className="w-10 h-10 object-contain" style={{ animation: 'candyFloat 4s ease-in-out infinite' }} />
            <span className={`${mobBrandSize} font-black whitespace-nowrap`} style={{ fontFamily: "'Fredoka', 'Comfortaa', Arial, sans-serif", letterSpacing: '1px' }}>
              <span style={{ background: 'linear-gradient(90deg, #F04A83, #9178D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>TASKA</span>
              <span style={{ background: 'linear-gradient(90deg, #FFB020, #F04A83)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}> UMMU QASEH</span>
            </span>
          </div>
          
          <div className="w-10 h-10"></div>
        </div>
        
        <style>{`
          @keyframes wiggle {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-5deg); }
            75% { transform: rotate(5deg); }
          }
        `}</style>
      </div>
      
      {/* Mobile Sidebar Drawer */}
      <div className={`lg:hidden fixed top-0 left-0 h-full w-72 bg-white shadow-2xl z-[70] transform transition-transform duration-300 ease-out flex flex-col ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button onClick={() => setMenuOpen(false)} className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl z-10">
          <X className="w-6 h-6" />
        </button>
        
        {/* User Info */}
        <div className="p-4 pt-14 border-b-4 border-gray-100 flex-shrink-0" style={{ background: 'linear-gradient(180deg, #FFF3C4 0%, #FFE3ED 50%, #E6E3F7 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="relative">
              <img src="/logo-destiny.png" alt="Logo" className="w-14 h-14 object-contain" />
            </div>
            <div>
              <p className="font-black text-gray-800">{user?.name}</p>
              <p className="text-sm font-bold text-blue-500"><RoleLabelDisplay role={user?.role} /></p>
              {user?.branch && <p className="text-xs font-bold text-green-500">📍 {user.branch}</p>}
            </div>
          </div>
        </div>
        
        {/* Menu Items - Scrollable with proper flex */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1" style={{ WebkitOverflowScrolling: 'touch' }}>
          {menuItems.map((item, i) => item.isGroup ? renderMenuGroup(item, true, i) : renderMenuItem(item, true, false, i))}
          
          {/* Logout Button inside scrollable area */}
          <div className="pt-4 mt-4 border-t-2 border-gray-100 pb-6">
            <button
              onClick={onLogout}
              className="w-full text-left px-4 py-3 rounded-2xl bg-gradient-to-r from-red-400 to-red-500 text-white font-bold flex items-center gap-3 shadow-lg shadow-red-200/50 hover:shadow-xl transition-all"
            >
              <LogOut className="w-5 h-5" />
              Log Keluar 👋
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-col w-64 h-screen fixed left-0 top-0" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF7FA 60%, #F7F3FF 100%)', borderRight: '1px solid #F5D9E8', boxShadow: '0 6px 20px rgba(240,74,131,0.08)' }}>
        {/* Candy top bar */}
        <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #F04A83 0%, #FFC93C 30%, #6FE7DD 60%, #9178D6 100%)' }} />
        
        <div className="p-6 flex-shrink-0">
          <div className="flex flex-col items-center mb-4">
            <div className="relative">
              <img 
                src="/logo-destiny.png" 
                alt="Logo" 
                className="w-24 h-24 object-contain mb-2 drop-shadow-xl"
                style={{ animation: 'candyFloat 4s ease-in-out infinite' }}
              />
              <div className="absolute -top-1 -right-2 text-xl" style={{ animation: 'candyBob 2.2s ease-in-out infinite' }}>🎈</div>
              <div className="absolute bottom-2 -left-2 text-lg" style={{ animation: 'candyBob 2.8s ease-in-out infinite 0.6s' }}>☀️</div>
            </div>
            <h1 className={`${deskBrandSize} font-black text-center whitespace-nowrap max-w-full`} style={{ fontFamily: "'Fredoka', 'Comfortaa', Arial, sans-serif", letterSpacing: '1px' }}>
              <span style={{ background: 'linear-gradient(90deg, #F04A83, #9178D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>TASKA</span>
              <span style={{ background: 'linear-gradient(90deg, #FFB020, #F04A83)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}> UMMU QASEH</span>
            </h1>
          </div>
          <div className="text-center pt-3" style={{ borderTop: '2px dashed #F5D9E8' }}>
            <p className="font-black" style={{ color: '#2B2E6B', fontFamily: "'Fredoka', sans-serif" }}>{user?.name}</p>
            <p className="text-sm font-bold" style={{ color: '#7357C9' }}><RoleLabelDisplay role={user?.role} /></p>
            {user?.branch && <p className="text-xs font-bold mt-1" style={{ color: '#2FA396' }}>📍 {user.branch}</p>}
          </div>
        </div>

        <style>{`
          @keyframes candyFloat {
            0%, 100% { transform: translateY(0) rotate(-2deg) scale(1); }
            50% { transform: translateY(-10px) rotate(2deg) scale(1.03); }
          }
          @keyframes candyBob {
            0%, 100% { transform: translateY(0) rotate(-8deg); }
            50% { transform: translateY(-6px) rotate(8deg); }
          }
          @keyframes candySpin {
            0%, 100% { transform: rotate(-15deg) scale(1); }
            50% { transform: rotate(15deg) scale(1.2); }
          }
          .candy-menu-item { transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.25s ease, box-shadow 0.25s ease; }
          .candy-menu-item:hover { transform: translateX(4px) scale(1.02); background: #FDEBF3; }
        `}</style>

        <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-2 space-y-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#ddd transparent', WebkitOverflowScrolling: 'touch' }}>
          {menuItems.map((item, i) => item.isGroup ? renderMenuGroup(item, false, i) : renderMenuItem(item, false, false, i))}
        </nav>

        <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid #E4DFF4' }}>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-full font-bold transition-all hover:-translate-y-0.5"
            style={{ background: '#FFE3ED', color: '#F04A83' }}
          >
            <LogOut className="w-5 h-5" />
            <span>Log Keluar 👋</span>
          </button>
        </div>
      </div>
    </>
  );
}

// Bottom Navigation for Mobile - Space Explorer Theme
export function SpaceBottomNav({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [ripple, setRipple] = useState(null);
  const [notifications, setNotifications] = useState({
    activities: 0,
    daily_logs: 0,
    nutrition: 0
  });
  
  // Fetch notifications for parent
  useEffect(() => {
    if (user?.role !== 'ibu_bapa') return;
    
    const fetchNotifications = async () => {
      try {
        const today = getLocalDateString();
        const lastViewed = JSON.parse(localStorage.getItem('parentLastViewed') || '{}');
        const todayStart = new Date().setHours(0, 0, 0, 0);
        
        const [activitiesRes, logsRes, nutritionRes] = await Promise.allSettled([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activities?date=${today}`, {
            headers: { 'Authorization': `Bearer ${secureStorage.getItem('token')}` }
          }).then(r => r.json()),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/daily-logs?date=${today}`, {
            headers: { 'Authorization': `Bearer ${secureStorage.getItem('token')}` }
          }).then(r => r.json()),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nutrition?date=${today}`, {
            headers: { 'Authorization': `Bearer ${secureStorage.getItem('token')}` }
          }).then(r => r.json())
        ]);
        
        setNotifications({
          activities: (activitiesRes.status === 'fulfilled' && lastViewed.activities < todayStart) ? (activitiesRes.value?.length || 0) : 0,
          daily_logs: (logsRes.status === 'fulfilled' && lastViewed.daily_logs < todayStart) ? (logsRes.value?.length || 0) : 0,
          nutrition: (nutritionRes.status === 'fulfilled' && lastViewed.nutrition < todayStart) ? (nutritionRes.value?.length || 0) : 0
        });
      } catch (e) {
        console.log("Notification fetch error");
      }
    };
    
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.role]);
  
  // Get nav items based on user role with emoji and colors
  const getNavItems = () => {
    const role = user?.role;
    
    if (role === 'ibu_bapa') {
      return [
        { path: "/", icon: Home, label: "Utama", emoji: "🏠", color: "#F04A83", notifyKey: null },
        { path: "/activities", icon: CalendarDays, label: "Aktiviti", emoji: "📅", color: "#60A5FA", notifyKey: "activities" },
        { path: "/daily-logs", icon: FileText, label: "Log", emoji: "📝", color: "#FBBF24", notifyKey: "daily_logs" },
        { path: "/nutrition", icon: Utensils, label: "Menu", emoji: "🍽️", color: "#34D399", notifyKey: "nutrition" },
        { path: "/settings", icon: Settings, label: "Tetapan", emoji: "⚙️", color: "#F472B6", notifyKey: null },
      ];
    }
    
    if (role === 'guru') {
      return [
        { path: "/", icon: Home, label: "Utama", emoji: "🏠", color: "#F04A83" },
        { path: "/children", icon: Baby, label: "Kanak", emoji: "👶", color: "#A78BFA" },
        { path: "/activities", icon: CalendarDays, label: "Aktiviti", emoji: "📅", color: "#60A5FA" },
        { path: "/daily-logs", icon: FileText, label: "Log", emoji: "📝", color: "#FBBF24" },
        { path: "/settings", icon: Settings, label: "Tetapan", emoji: "⚙️", color: "#F472B6" },
      ];
    }
    
    if (role === 'admin' || role === 'branch_manager') {
      return [
        { path: "/", icon: Home, label: "Utama", emoji: "🏠", color: "#F04A83" },
        { path: "/children", icon: Baby, label: "Kanak", emoji: "👶", color: "#A78BFA" },
        { path: "/billing", icon: Receipt, label: "Bil", emoji: "💰", color: "#34D399" },
        { path: "/reports", icon: BarChart3, label: "Laporan", emoji: "📊", color: "#60A5FA" },
        { path: "/settings", icon: Settings, label: "Tetapan", emoji: "⚙️", color: "#F472B6" },
      ];
    }
    
    // Superadmin
    return [
      { path: "/", icon: Home, label: "Utama", emoji: "🏠", color: "#F04A83" },
      { path: "/children", icon: Baby, label: "Kanak", emoji: "👶", color: "#A78BFA" },
      { path: "/billing", icon: Receipt, label: "Bil", emoji: "💰", color: "#34D399" },
      { path: "/reports", icon: BarChart3, label: "Laporan", emoji: "📊", color: "#60A5FA" },
      { path: "/settings", icon: Settings, label: "Tetapan", emoji: "⚙️", color: "#F472B6" },
    ];
  };
  
  const navItems = getNavItems();
  
  const handleClick = (path, index, notifyKey) => {
    setRipple(index);
    setTimeout(() => setRipple(null), 400);
    
    // Mark as viewed if it's a notification item
    if (notifyKey && user?.role === 'ibu_bapa') {
      const lastViewed = JSON.parse(localStorage.getItem('parentLastViewed') || '{}');
      lastViewed[notifyKey] = new Date().getTime();
      localStorage.setItem('parentLastViewed', JSON.stringify(lastViewed));
      setNotifications(prev => ({ ...prev, [notifyKey]: 0 }));
    }
    
    navigate(path);
  };
  
  return (
    <nav 
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50"
      style={{
        paddingBottom: "max(8px, env(safe-area-inset-bottom))"
      }}
    >
      {/* Background candy glassmorphism */}
      <div 
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,240,247,0.98) 60%, rgba(247,243,255,0.98) 100%)",
          borderRadius: "30px 30px 0 0",
          boxShadow: "0 -8px 32px rgba(145, 120, 214, 0.22), 0 -2px 8px rgba(240, 74, 131, 0.12)",
          backdropFilter: "blur(20px)",
          borderTop: "3px solid rgba(240, 74, 131, 0.25)"
        }}
      />
      
      {/* Candy stripe accent line */}
      <div 
        className="absolute top-0 left-4 right-4 h-1.5 rounded-full overflow-hidden"
        style={{
          background: "repeating-linear-gradient(45deg, #F04A83 0px, #F04A83 8px, #FFC93C 8px, #FFC93C 16px, #6FE7DD 16px, #6FE7DD 24px, #9178D6 24px, #9178D6 32px)",
          backgroundSize: "200% 100%",
          animation: "candyStripe 8s linear infinite"
        }}
      />
      
      {/* Floating candy decorations */}
      <div className="absolute -top-3 left-6 text-lg" style={{ animation: 'candyBobNav 2.4s ease-in-out infinite' }}>🎈</div>
      <div className="absolute -top-2 right-8 text-sm" style={{ animation: 'candyBobNav 2.8s ease-in-out infinite 0.4s' }}>🍬</div>
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 text-lg" style={{ animation: 'candyBobNav 3.2s ease-in-out infinite 0.8s' }}>☀️</div>
      
      <style>{`
        @keyframes candyStripe {
          0% { background-position: 0% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes candyBobNav {
          0%, 100% { transform: translateY(0) rotate(-10deg); }
          50% { transform: translateY(-5px) rotate(10deg); }
        }
        @keyframes jellyPop {
          0% { transform: scale(1, 1); }
          30% { transform: scale(1.15, 0.85); }
          50% { transform: scale(0.92, 1.08); }
          70% { transform: scale(1.05, 0.95); }
          100% { transform: scale(1, 1); }
        }
      `}</style>

      <div className="relative flex justify-around items-center px-2 py-2">
        {navItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
                          (item.path !== "/" && location.pathname.startsWith(item.path));
          const notifyCount = item.notifyKey ? (notifications[item.notifyKey] || 0) : 0;
          
          return (
            <button
              key={item.path}
              onClick={() => handleClick(item.path, index, item.notifyKey)}
              className="relative flex flex-col items-center gap-0.5 px-3 py-2 rounded-2xl transition-all duration-300"
              style={{
                minWidth: "60px",
                transform: isActive ? "translateY(-8px) scale(1.1)" : "scale(1)",
              }}
            >
              {/* Active background blob */}
              {isActive && (
                <div 
                  className="absolute inset-0 rounded-2xl animate-pulse"
                  style={{
                    background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}10 100%)`,
                    border: `2px solid ${item.color}40`,
                  }}
                />
              )}
              
              {/* Ripple effect */}
              {ripple === index && (
                <div 
                  className="absolute inset-0 rounded-2xl animate-ping"
                  style={{ background: `${item.color}30` }}
                />
              )}
              
              {/* Icon container - candy bubble */}
              <div 
                className="relative z-10 flex items-center justify-center transition-all duration-300"
                style={{
                  width: isActive ? "46px" : "36px",
                  height: isActive ? "46px" : "36px",
                  borderRadius: "50%",
                  background: isActive 
                    ? `linear-gradient(145deg, ${item.color} 0%, ${item.color}AA 100%)` 
                    : "transparent",
                  boxShadow: isActive 
                    ? `0 6px 16px ${item.color}55, inset 0 -3px 6px rgba(0,0,0,0.12), inset 0 3px 6px rgba(255,255,255,0.45)` 
                    : "none",
                  animation: isActive ? "jellyPop 0.6s ease-out" : "none",
                }}
              >
                {isActive ? (
                  <span className="text-lg">{item.emoji}</span>
                ) : (
                  <Icon 
                    className="transition-all duration-300"
                    style={{ 
                      width: "20px",
                      height: "20px",
                      color: "#9CA3AF",
                      strokeWidth: 2
                    }}
                  />
                )}
                
                {/* Notification Bubble */}
                {notifyCount > 0 && (
                  <div 
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[10px] font-black shadow-lg animate-bounce"
                    style={{
                      background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                      padding: "0 4px",
                      border: "2px solid white"
                    }}
                  >
                    {notifyCount > 9 ? "9+" : notifyCount}
                  </div>
                )}
                
                {/* Lollipop on active (only if no notification) */}
                {isActive && notifyCount === 0 && (
                  <div className="absolute -top-1.5 -right-1.5" style={{ animation: 'candyBobNav 2s ease-in-out infinite' }}>
                    <Lollipop className="w-3.5 h-3.5 text-pink-500" style={{ filter: 'drop-shadow(0 1px 2px rgba(240,74,131,0.4))' }} />
                  </div>
                )}
              </div>
              
              {/* Label */}
              <span 
                className="relative z-10 font-bold transition-all duration-300 whitespace-nowrap"
                style={{ 
                  color: isActive ? item.color : "#9CA3AF",
                  fontSize: isActive ? "10px" : "9px",
                  marginTop: isActive ? "3px" : "1px",
                  fontFamily: "'Fredoka', 'Comfortaa', sans-serif",
                  letterSpacing: "0.3px",
                  textShadow: isActive ? `0 1px 2px ${item.color}30` : "none"
                }}
              >
                {item.label}
              </span>
              
              {/* Active indicator dot */}
              {isActive && (
                <div 
                  className="absolute -bottom-0.5 w-1 h-1 rounded-full animate-pulse"
                  style={{ background: item.color }}
                />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Bottom safe area fill */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-2"
        style={{ background: "rgba(255,245,250,0.98)" }}
      />
    </nav>
  );
}

// Layout wrapper
export function PageLayout({ children, user, onLogout, hasAccess }) {
  return (
    <div className="min-h-screen pb-20 lg:pb-0" style={{ background: '#F4F2FB' }}>
      <Sidebar user={user} onLogout={onLogout} hasAccess={hasAccess} />
      <div className="pt-16 lg:pt-0 lg:ml-64" style={{ paddingTop: 'max(64px, calc(env(safe-area-inset-top) + 56px))' }}>
        <div className="p-4 lg:p-8">{children}</div>
      </div>
      <SpaceBottomNav user={user} />
      <style>{`
        @media (min-width: 1024px) {
          .lg\\:pt-0 { padding-top: 0 !important; }
        }
      `}</style>
    </div>
  );
}
